import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as APIHelper from '../../../Helper/apiFetch';
import { getSessionToken, listenForSession } from '../../../Helper/native';

/**
 * Custom hook to fetch and manage campaign data
 */
export const useCampaignData = (hasAccessToken, setHasAccessToken) => {
  const navigate = useNavigate();
  // Keep the (button) loader on until the access token is assigned AND the
  // campaign API responds. The page itself still renders immediately.
  const [shimmerLoader, setShimmerLoader] = useState(true);
  const [campaignData, setCampaignData] = useState(null);
  const [campaignConfig, setCampaignConfig] = useState(null);
  const [loadingLevel, setLoadingLevel] = useState({
    levelOne: false,
    levelTwo: false,
    levelThree: false,
  });

  useEffect(() => {
    let tokenPoller = null;
    let stopSessionListener = null;

    const fetchCampaignData = async () => {
      setShimmerLoader(true);
      const currentToken = sessionStorage.getItem('AccessToken');
      
      if (!currentToken) {
        setShimmerLoader(false);
        return;
      }

      try {
        // Fetch both campaign data and config in parallel
        const [resData, configResponse] = await Promise.all([
          APIHelper.topFive(),
          // APIHelper.getCampaignConfig().catch(() => null), 
        ]);
        if (resData.error) {
          navigate('/error');
          return;
        }

        // Process campaign config
        if (configResponse && !configResponse.error && configResponse.data) {
          setCampaignConfig(configResponse.data);
        }

        const totalTxnAmount = resData?.totalTxnAmount || 0;
        const totalVoucherTxnAmount = resData?.totalVoucherTxnAmt || 0;
        const progress = Math.min((totalTxnAmount / 100000) * 100, 100);
        const voucherProgress = Math.min((totalVoucherTxnAmount / 10000) * 100, 100);
        
        setCampaignData({
          ...resData,
          progress,
          voucherProgress,
        });
      } catch (error) {
        console.error('Error fetching campaign data:', error);
      } finally {
        setShimmerLoader(false);
      }
    };

    const ensureTokenAndFetch = () => {
      // Prefer any token already in session storage, otherwise read it
      // synchronously from the native session bridge (window.__GOSATS_SESSION__).
      const token = sessionStorage.getItem('AccessToken') || getSessionToken();
      if (token) {
        setHasAccessToken(true);
        fetchCampaignData();
      } else {
        // No token yet: keep the button loader on and listen/poll in the
        // background until a token arrives (via the native session message, in
        // session storage, or appended to the URL by the native app), then
        // fetch the campaign data.
        const onTokenReady = () => {
          if (tokenPoller) clearInterval(tokenPoller);
          if (stopSessionListener) stopSessionListener();
          setHasAccessToken(true);
          fetchCampaignData();
        };

        stopSessionListener = listenForSession(onTokenReady);

        tokenPoller = setInterval(() => {
          const urlToken = new URL(window.location.href).searchParams.get('token');
          if (urlToken) {
            sessionStorage.setItem('AccessToken', urlToken);
          }
          const freshToken = sessionStorage.getItem('AccessToken') || getSessionToken();
          if (freshToken) {
            onTokenReady();
          }
        }, 500);
      }
    };

    ensureTokenAndFetch();

    return () => {
      if (tokenPoller) {
        clearInterval(tokenPoller);
      }
      if (stopSessionListener) {
        stopSessionListener();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  return {
    shimmerLoader,
    campaignData,
    setCampaignData,
    campaignConfig,
    loadingLevel,
    setLoadingLevel,
  };
};
